<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewWarrants' }" class="mb-0 main-identifier fw-semibold text-decoration-none" title="Go back to Warrants">
        Warrants
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Warrant Information</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer-wrapper">
        <div class="form-outer">
          <form>
            <div class="crud-form position-relative">

              <div class="form-inner mb-2">
                <div class="form-header d-flex justify-content-between align-items-center">
                  <!-- <h4>View Warrant Information (Step {{ step }} of {{ totalSteps }})</h4> -->
                  <h1 class="mb-0">Warrant Information</h1>
                </div>

                <div class="warrant-content-area position-relative">
                  <div v-if="loadingWarrant" class="loading-overlay text-center">
                    <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                    <p class="mt-2 mb-0 text-muted">Loading warrant information...</p>
                  </div>

                  <div v-if="errorMessage && !loadingWarrant" class="alert alert-warning" role="alert">
                    {{ errorMessage }}
                  </div>

                  <div>
                    <!-- <h2 class="field-head">B. Budget</h2> -->
                    <div class="row mb-3 warrant-item-wrapper step-2 justify-content-center">
                      <div class="row content-wrapper section-border h-100 w-100">
                        <div class="col d-flex">
                          <div class="d-flex flex-column justify-content-center h-100 w-100">
                            <label class="form-label fw-bold fixed-label">Warrant Number</label>
                            <p type="text" class="form-control view-only-input text-center" readonly>
                              {{ form.warrantNumber || 'N/A' }}
                            </p>
                          </div>
                        </div>
                        <div class="col d-flex">
                          <div class="d-flex flex-column justify-content-center h-100 w-100">
                            <label class="form-label fw-bold fixed-label">Issue Date</label>
                            <p type="text" class="form-control view-only-input text-center" readonly>
                              {{ formatIssueDate(form.issueDate) || 'N/A' }}
                            </p>
                          </div>
                        </div>
                        <div class="col d-flex">
                          <div class="d-flex flex-column justify-content-center h-100 w-100">
                            <label class="form-label fw-bold fixed-label">MDA</label>
                            <p type="text" class="form-control view-only-input text-center" readonly>
                              {{ form.mda || 'N/A' }}

                            </p>
                          </div>
                        </div>
                        <div class="col d-flex">
                          <div class="d-flex flex-column justify-content-center h-100 w-100">
                            <label class="form-label fw-bold fixed-label">Organizational Code</label>
                            <p type="text" class="form-control view-only-input text-center" readonly>
                              {{ form.orgCode || 'N/A' }}
                            </p>
                          </div>
                        </div>
                        <div class="col d-flex">
                          <div class="d-flex flex-column justify-content-center h-100 w-100">
                            <label class="form-label fw-bold fixed-label">Total Amount Released</label>
                            <input type="text" class="form-control view-only-input text-center"
                              :value="'₦' + Number(form.amountReleased || 0).toLocaleString()" readonly>
                          </div>
                        </div>
                        <div class="col no-border d-flex">
                          <div class="d-flex flex-column justify-content-center h-100 w-100">
                            <label class="form-label fw-bold fixed-label">Payment Voucher Available Balance</label>
                            <input type="text" class="form-control view-only-input text-center"
                              :value="'₦' + Number(form.pvAvailableBalance || 0).toLocaleString()" readonly>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="row warrant-item-wrapper justify-content-center">
                      <div class="row content-wrapper h-100 w-100">
                        <div class="col-12 d-flex">
                          <div class="d-flex flex-column justify-content-center h-100 w-100">
                            <label class="form-label fw-bold fixed-label">Description</label>
                            <p type="text" class="form-control view-only-input text-center" style="white-space: pre-wrap;" readonly>
                              {{ form.details || 'N/A' }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="table-responsive mt-4">
                      <h5 class="mb-3">Economic Line Items</h5>
                      <table class="table table-bordered text-center align-middle">
                        <thead class="table-light">
                          <tr>
                            <th>Eco Code</th>
                            <th>Title</th>
                            <th>Amount Budgeted (₦)</th>
                            <th>Amount Released (₦)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, index) in economicItems" :key="index"
                            :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                            <td>
                              {{ item.ecoCode }}
                            </td>
                            <td>
                              {{ item.title }}
                            </td>
                            <td>
                              {{ Number(item.amountBudgeted || 0).toLocaleString() }}
                            </td>
                            <td>
                              {{ Number(item.amountReleased || 0).toLocaleString() }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div class="mt-4 me-1" v-if="!form.voidedWarrant && (canGeneratePV || canDownloadWarrant || canVoidWarrant)">
                    <div class="d-flex justify-content-end gap-2">
                      <button v-if="canGeneratePV" type="button" class="btn btn-outline-secondary" @click="openPVModal"
                        :disabled="loadingWarrant" aria-label="View Payment Vouchers">
                        View Payment Vouchers
                      </button>
                      <span v-if="canGeneratePV" :title="generatePVDisabledReason">
                        <button type="button" class="btn btn-outline-primary generate-btn-app"
                          :class="{ 'disabled-generate-btn': isGeneratePVDisabled }" @click="generatePV"
                          :disabled="isGeneratePVDisabled" aria-live="polite"
                          :aria-label="loadingPV ? 'Generating Form...' : 'Generate PV'">
                          <span v-if="loadingPV" class="spinner-border spinner-border-sm me-2" role="status"
                            aria-hidden="true"></span>
                          {{ loadingPV ? 'Generating Form...' : 'Generate PV' }}
                        </button>
                      </span>
                      <button v-if="canDownloadWarrant" type="button" class="btn btn-outline-primary download-btn-app"
                        @click="downloadWarrant" :disabled="loadingDownload || loadingWarrant" aria-live="polite"
                        :aria-label="loadingDownload ? 'Downloading...' : 'Download Warrant'">
                        <span v-if="loadingDownload" class="spinner-border spinner-border-sm me-2" role="status"
                          aria-hidden="true"></span>
                        {{ loadingDownload ? 'Downloading...' : 'Download Warrant' }}
                      </button>
                      <button v-if="canVoidWarrant" type="button" class="btn btn-outline-danger void-btn-app"
                        @click="voidWarrant" :disabled="loadingVoid || loadingWarrant" aria-live="polite"
                        :aria-label="loadingVoid ? 'Voiding...' : 'Void Warrant'">
                        <span v-if="loadingVoid" class="spinner-border spinner-border-sm me-2" role="status"
                          aria-hidden="true"></span>
                        {{ loadingVoid ? 'Voiding...' : 'Void Warrant' }}
                      </button>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </form>
        </div>
        <router-link :to="{ name: 'ViewGeneratePVWithoutWarrant' }" v-if="canGeneratePV && !loadingWarrant"
          class="d-flex justify-content-start align-items-center text-decoration-none">
          <span class="material-symbols-outlined me-1 align-items-center">
            receipt_long
          </span>
          Generate New PV Without Warrant
        </router-link>
      </div>
    </div>

    <!-- Payment Vouchers Modal -->
    <div v-if="showPVModal" class="pv-modal-overlay" @click="closePVModal">
      <div class="pv-modal" @click.stop>
        <div class="pv-modal-header">
          <h5 class="mb-0">Payment Vouchers for Warrant {{ form.warrantNumber }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closePVModal"></button>
        </div>

        <div class="pv-modal-body">
          <div v-if="loadingPVList" class="text-center py-4">
            <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
            <p class="mt-2 mb-0 text-muted">Loading payment vouchers...</p>
          </div>
          <div v-else-if="paymentVouchers.length === 0" class="alert alert-info text-center">
            No Payment Vouchers found.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-bordered text-center align-middle">
              <thead class="table-light">
                <tr>
                  <th>PV Number</th>
                  <th>Payee</th>
                  <th>Gross Amount</th>
                  <th>Net Amount</th>
                  <th>Payment Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pv in paymentVouchers" :key="pv.id">
                  <td>
                    {{ pv.pvNumber }}
                  </td>
                  <td class="text-start">
                    {{ pv.payee }}
                  </td>
                  <td>
                    {{ formatCurrency(pv.grossAmount) }}
                  </td>
                  <td>
                    {{ formatCurrency(pv.netAmount) }}
                  </td>
                  <td>
                    {{ pv.paymentDate }}
                  </td>
                  <td>
                    <span :class="['badge', getPvStatusClass(pv)]">{{ getPvStatusLabel(pv) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
// import { useRouter } from 'vue-router';

// const router = useRouter();


const root_api_url = process.env.VUE_APP_API_ROOT_URL


const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const warrantId = route.params.warrantId
const loadingPV = ref(false)
const loadingWarrant = ref(true)
const loadingPVList = ref(false)
const errorMessage = ref('')
const showPVModal = ref(false)
const loadingDownload = ref(false)
const loadingVoid = ref(false)

const hasRole = (role) => !!(userStore.profile?.role || {})[role]

// Roles gated on the sidebar (src/components/dashboard/includes/AppSidebar.vue) - a
// "free" user has none of these flags set, and sees the most functions/menu items.
const allRoles = [
  'qssu',
  'dirOfBudget',
  'perSec',
  'commOfBAndP',
  'registryOfficer',
  'groupHead',
  'groupMember',
  'initiator'
]

const isFreeUser = computed(() => {
  const profile = userStore.profile?.role || {}
  return Object.keys(profile).every(r => !allRoles.includes(r) || profile[r] === false)
})

// Check if user can generate PV (bizClientId === 1001 AND pvInitiator role)
const canGeneratePV = computed(() => {
  // return userStore.bizClientId === 1001 && hasRole('pvInitiator')
  return userStore.bizClientId === 1001

})

const canDownloadWarrant = computed(() => {
  return hasRole('groupHead') || hasRole('groupMember')
})

const canVoidWarrant = computed(() => {
  // TODO: update with the correct role check
  return hasRole('groupHead') || (userStore.bizClientId === 1001 && isFreeUser.value)
})

const hasPositivePvBalance = computed(() => Number(form.pvAvailableBalance || 0) > 0)

const isGeneratePVDisabled = computed(() => {
  return loadingPV.value || loadingWarrant.value || !hasPositivePvBalance.value
})

const generatePVDisabledReason = computed(() => {
  if (!hasPositivePvBalance.value) {
    return 'No payment voucher balance is available, so a new payment voucher cannot be generated.'
  }
  return ''
})


// Reactive form data
const form = reactive({
  id: '',
  appraisalId: '',
  appraisalInitiatorId: '',
  warrantNumber: '',
  mda: '',
  geoCode: '',
  originatingMda: '',
  approvalAuthority: '',
  natureOfExpenditure: '',
  amountApproved: '',
  purposeOfRequest: '',
  approvedBudgetAmount: '',
  amtReleasedToDate: '',
  amountReleased: '',
  pvAvailableBalance: '',
  balance: '',
  performance: '',
  quarter: '',
  budgetYear: '',
  appraisalInitiatedDate: '',
  warrantInitiatedDate: '',
  noOfAppLimitPerQtr: '',
  noOfAuthIssuedInQtr: '',
  noOfAuthLeftInQtr: '',
  qtrAuthLimitAmt: '',
  approvedRecurrentAmount: '',
  approvedCapitalAmount: '',
  approvalBalance: '',
  evalRemarks: '',
  amtRecommended: '',
  sectoralHeadRemarks: '',
  dobRemarks: '',
  commissionerRemarks: '',
  orgCode: '',
  permSecRemarks: '',
  warrantInitiatorId: '',
  sectoralHeadId: '',
  sectoralHeadRemarkDate: '',
  dobId: '',
  dobRemarkDate: '',
  permSecId: '',
  permSecRemarksDate: '',
  commissionerId: '',
  commRemarkDate: '',
  issueDate: '',
  warrantApprovedInd: 0,
  ticketId: '',
  appraisalRejectedInd: 0,
  returnedAppraisalInd: 0,
  importedAppraisalInd: 0,
  initiatorName: '',
  sectoralHeadName: '',
  dobName: '',
  psName: '',
  commissionerName: '',
  permSecExcsAppLmtReason: '',
  permSecBPAmtPerQtr: '',
  permSecRecommendedInd: 0,
  errorMessage: '',
  hasWarrantInd: 0,
  pvMemoId: '',
  payVoucherId: '',
  rejected: false,
  returnedAppraisal: false,
  initiated: false,
  sectorHeadTreated: false,
  dirOfBudgetTreated: false,
  permSecTreated: false,
  importedAppraisal: false,
  commBudgetAndPlanningTreated: false,
  permSecRecommended: false,
  approvalAuthorityId: '',
  mdaId: '',
  evalApprovedRecurrentAmount: '',
  evalApprovedCapitalAmount: '',
  evalApprovalBalance: '',
  groupTypeId: '',
  memoFileNumber: '',
  details: '',
  voidedWarrant: false,
})

const economicItems = ref([])
const paymentVouchers = ref([])

const fetchWarrant = async () => {
  const token = Cookies.get('auth_token')
  loadingWarrant.value = true
  errorMessage.value = ''
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/warrant/${warrantId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })



    form.id = data.id || ''
    form.appraisalId = data.appraisalId || ''
    form.warrantNumber = data.warrantNumber || data.warrantNo || ''
    form.mda = data.mdaName || data.mda || ''
    form.issueDate = data.issueDate || ''
    form.orgCode = data.orgCode || ''
    form.geoCode = data.geoCode ?? ''

    // console.log(data);
    form.originatingMda = data.originatingMda;
    form.approvalAuthority = data.approvalAuthority;
    form.natureOfExpenditure = data.natureOfExpenditure

    form.amountApproved = data.amountApproved;
    form.purposeOfRequest = data.purposeOfRequest;
    form.approvedBudgetAmount = data.approvedBudgetAmount;
    form.amtReleasedToDate = data.amtReleasedToDate ?? data.amountReleased;
    form.amountReleased = data.amountReleased;
    form.pvAvailableBalance = data.payVoucherBalance;
    form.balance = data.balance;
    form.performance = data.performance;
    form.quarter = data.quarter;
    form.budgetYear = data.budgetYear;
    form.appraisalInitiatedDate = data.appraisalInitiatedDate;
    form.warrantInitiatedDate = data.warrantInitiatedDate;
    form.noOfAppLimitPerQtr = data.noOfAppLimitPerQtr;
    form.noOfAuthIssuedInQtr = data.noOfAuthIssuedInQtr;
    form.noOfAuthLeftInQtr = data.noOfAuthLeftInQtr;
    form.qtrAuthLimitAmt = data.qtrAuthLimitAmt;
    form.approvedRecurrentAmount = data.approvedRecurrentAmount;
    form.approvedCapitalAmount = data.approvedCapitalAmount;
    form.approvalBalance = data.approvalBalance;
    form.evalRemarks = data.evalRemarks;
    form.orgCode = data.orgCode;
    form.warrantInitiatorId = data.warrantInitiatorId;
    form.appraisalInitiatorId = data.appraisalInitiatorId;
    form.issueDate = data.issueDate;
    form.warrantApprovedInd = data.warrantApprovedInd ?? 0;
    form.ticketId = data.ticketId;
    form.appraisalRejectedInd = data.appraisalRejectedInd ?? 0;
    form.returnedAppraisalInd = data.returnedAppraisalInd ?? 0;
    form.importedAppraisalInd = data.importedAppraisalInd ?? 0;
    form.initiatorName = data.initiatorName;
    form.sectoralHeadName = data.sectoralHeadName;
    form.dobName = data.dobName;
    form.psName = data.psName;
    form.commissionerName = data.commissionerName;
    form.permSecExcsAppLmtReason = data.permSecExcsAppLmtReason;
    form.permSecBPAmtPerQtr = data.permSecBPAmtPerQtr;
    form.permSecRecommendedInd = data.permSecRecommendedInd ?? 0;
    form.errorMessage = data.errorMessage;
    form.hasWarrantInd = data.hasWarrantInd ?? 0;
    form.pvMemoId = data.pvMemoId;
    form.payVoucherId = data.payVoucherId;
    form.rejected = !!data.rejected;
    form.returnedAppraisal = !!data.returnedAppraisal;
    form.initiated = !!data.initiated;
    form.sectorHeadTreated = !!data.sectorHeadTreated;
    form.dirOfBudgetTreated = !!data.dirOfBudgetTreated;
    form.permSecTreated = !!data.permSecTreated;
    form.importedAppraisal = !!data.importedAppraisal;
    form.commBudgetAndPlanningTreated = !!data.commBudgetAndPlanningTreated;
    form.permSecRecommended = !!data.permSecRecommended;
    form.approvalAuthorityId = data.approvalAuthorityId;
    form.mdaId = data.mdaId;
    if (data.sectoralHeadRemarks) {
      form.sectoralHeadRemarks = data.sectoralHeadRemarks;
      form.amtRecommended = data.amtRecommended;
      form.dobRemarks = data.dobRemarks;
      form.permSecRemarks = data.permSecRemarks;
      form.commissionerRemarks = data.commissionerRemarks;
      form.commissionerId = data.commissionerId;
      form.commRemarkDate = data.commRemarkDate;
      form.sectoralHeadId = data.sectoralHeadId;
      form.sectoralHeadRemarkDate = data.sectoralHeadRemarkDate;
      form.dobId = data.dobId;
      form.dobRemarkDate = data.dobRemarkDate;
      form.permSecId = data.permSecId;
      form.permSecRemarksDate = data.permSecRemarksDate;
    }

    form.groupTypeId = data.groupTypeId
    form.details = data.details || ''

    form.evalApprovedRecurrentAmount = data.evalApprovedRecurrentAmount;
    form.evalApprovedCapitalAmount = data.evalApprovedCapitalAmount;
    form.evalApprovalBalance = data.evalApprovalBalance;
    form.memoFileNumber = data.memoFileNumber;
    form.voidedWarrant = !!data.voidedWarrant;

    economicItems.value = (data.warrantEconomicLineItems || []).map(dto => ({
      id: dto.id,
      //ecoCode: dto.economicLineItemDto.economicCode,
      ecoCode: dto.ecoCode,
      title: dto.title,
      mdaLineItemBudgetId: dto.mdaLineItemBudgetId,
      amountBudgeted: dto.amountBudgeted,
      amountReleased: dto.amountReleased,
      balance: dto.balance,
      amountRequired: dto.amountRequired,
      amountApproved: dto.amountApproved,
      year: dto.year,
      quarter: dto.quarter,
      eliAmountRecommended: dto.amountRecommended || 0
    }));

    // console.log('Warrant economic items:', economicItems.value);




  } catch (err) {
    console.error('Error loading ECO items:', err)

    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    const message = err?.response?.data?.errorMessage || err?.response?.data?.error || err?.response?.data?.message || 'Failed to load warrant information. Kindly contact administrator.'
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${message}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });

    errorMessage.value = message
  } finally {
    loadingWarrant.value = false
  }
}

const fetchPVsByWarrant = async () => {
  const token = Cookies.get('auth_token')
  loadingPVList.value = true
  try {
    const { data } = await axios.get(`${root_api_url}/payment/pv-by-warrant/${warrantId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    // Handle case where API returns a string message instead of an array
    paymentVouchers.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Error loading payment vouchers:', err)
    // Don't show error message - just leave list empty
    paymentVouchers.value = []
  } finally {
    loadingPVList.value = false
  }
}

const openPVModal = async () => {
  showPVModal.value = true
  await fetchPVsByWarrant()
}

const closePVModal = () => {
  showPVModal.value = false
}

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return ''
  const n = Number(val)
  if (isNaN(n)) return ''
  return '₦' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatIssueDate = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const day = date.getDate()
  const month = date.toLocaleString('en-US', { month: 'long' })
  const year = date.getFullYear()

  const getOrdinal = (number) => {
    const remainder = number % 100
    if (remainder >= 11 && remainder <= 13) return `${number}th`
    switch (number % 10) {
      case 1:
        return `${number}st`
      case 2:
        return `${number}nd`
      case 3:
        return `${number}rd`
      default:
        return `${number}th`
    }
  }

  return `${getOrdinal(day)} ${month} ${year}`
}

const getPvStatusLabel = (pv) => {
  if (pv?.returned === true) return 'Returned'
  if (pv?.completed === true) return 'Completed'
  return 'Pending'
}

const getPvStatusClass = (pv) => {
  if (pv?.returned === true) return 'bg-warning'
  if (pv?.completed === true) return 'bg-success'
  return 'bg-secondary'
}

// const totalRecommended = computed(() =>
//   economicItems.value.reduce((sum, i) => sum + (i.eliAmountRecommended || 0), 0)
// );


// const warrantStatus = computed(() => {
//   // compute the status
//   let status

//   if (isApproved.value) {
//     status = 'Completed'
//   } else if (isRejected.value) {
//     status = 'Rejected'
//   } else if (isReturned.value) {
//     status = 'Returned'
//   } else {
//     status = 'Pending'
//   }
//   // log the raw values and the result
//   // console.log(
//   //   '🧪 warrantStatus computed:',
//   //   {
//   //     dobRemarks: form.dobRemarks,
//   //     sectoralHeadRemarks: form.sectoralHeadRemarks,
//   //     commissionerRemarks: form.commissionerRemarks,
//   //     permSecRemarks: form.permSecRemarks,
//   //     status
//   //   }
//   // );

//   return status;
// });


onMounted(async () => {
  if (!userStore.profile?.role || ((userStore.profile?.role?.groupHead || userStore.profile?.role?.groupMember) && !userStore.profile?.groupTypeId)) {
    await userStore.fetchProfile()
  }
  fetchWarrant()
  document.title = "View Warrant Information"
})


// function onAmountRequiredChange(idx) {
//   const item = economicItems.value[idx];
//   const available = item.balance;

//   // if within balance: just subtract
//   item.balance = available - item.amountRequired;
//   return;

//   // console.log(item.balance);

//   // else: overflow
//   // const overflow = item.amountRequired - available;

//   // // clamp this item
//   // item.amountRequired = available;
//   // item.balance = 0;

//   // find the next ECO option that's not yet used
//   // const nextOpt = ecoOptions.value.find(
//   //   o => !economicItems.value.some(e => e.id === o.id)
//   // );

//   // if (!nextOpt) {
//   //   Swal.fire({
//   //     icon: 'error',
//   //     title: 'No more line items',
//   //     text: 'You have exceeded all available ECO items.'
//   //   });
//   //   return;
//   // }

//   // ② Insert a new ECO row immediately after the current one
//   // economicItems.value.splice(idx + 1, 0, {
//   //   id: nextOpt.id,
//   //   ecoCode: nextOpt.ecoCode,
//   //   title: nextOpt.title,
//   //   approvedBudget: nextOpt.approvedBudget,
//   //   releasedToDate: nextOpt.releasedToDate,
//   //   balance: nextOpt.balance - overflow,
//   //   amountRequired: overflow
//   // });
// }


const generatePV = async () => {
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
  loadingPV.value = true

  try {
    const targetId = form.id || warrantId
    if (!targetId) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Warrant ID is missing.</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
      return
    }

    // await router.push({ name: 'ViewGeneratePVWithWarrant', params: { warrantId: targetId } })
    await router.push({ name: 'ViewGeneratePVWithWarrantTemp', params: { warrantId: targetId } })
  } catch (error) {
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>Unable to open PV generation page.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loadingPV.value = false
  }
}

const voidWarrant = async () => {
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  const confirm = await Swal.fire({
    title: 'Void Warrant',
    text: `Are you sure you want to void warrant ${form.warrantNumber}? This action cannot be undone.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, void it',
    cancelButtonText: 'Cancel',
  })

  if (!confirm.isConfirmed) return

  loadingVoid.value = true
  const token = Cookies.get('auth_token')
  const targetWarrantId = warrantId || form.id

  if (!targetWarrantId) {
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>Warrant ID is missing.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    loadingVoid.value = false
    return
  }

  try {
    const { data } = await axios.get(
      `${root_api_url}/appraisal/warrant/budget/void/${targetWarrantId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    )

    if (data?.errorMessage) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${data.errorMessage}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'Warrant Voided',
      text: `Warrant ${form.warrantNumber} has been voided successfully.`,
      showConfirmButton: true,
    })
    router.push({ name: 'ViewWarrants' })
  } catch (error) {
    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to void warrant.'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    }
  } finally {
    loadingVoid.value = false
  }
}

const downloadWarrant = async () => {
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
  loadingDownload.value = true
  const token = Cookies.get('auth_token')
  const targetWarrantId = warrantId || form.id

  if (!targetWarrantId) {
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>Warrant ID is missing for warrant download.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    loadingDownload.value = false
    return
  }

  try {
    const response = await axios.get(
      `${root_api_url}/appraisal/warrant/pdf/2/${targetWarrantId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob'
      }
    )

    let downloadName = 'warrant.pdf' // default filename

    const disposition = response.headers['content-disposition']
    console.log('Content-Disposition header:', response.headers)

    if (disposition && disposition.includes('filename=')) {
      downloadName = disposition
        .split('filename=')[1]
        .replace(/"/g, '')
    }

    const blob = new Blob([response.data], { type: response.data.type })
    const fileName = `${downloadName}`

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, fileName)
    } else {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName || '')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  } catch (error) {
    if (error.response) {
      // responseType is 'blob', so error bodies come back as a Blob too.
      // Read it back as text/JSON to extract the real error message.
      let message = 'Unexpected error occurred. Kindly contact administrator'
      try {
        const data = error.response.data
        if (data instanceof Blob) {
          const text = await data.text()
          const parsed = JSON.parse(text)
          message = parsed.errorMessage || parsed.error || parsed.message || message
        } else {
          message = data?.errorMessage || data?.error || data?.message || message
        }
      } catch (e) {
        // leave default message if the body isn't JSON
      }

      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${message}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    }
  } finally {
    loadingDownload.value = false
  }
}

// const displayValue = (item) => {
//   const n = item.eliAmountRecommended ?? 0
//   return n
//     .toString()
//     .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
// }

// const onInputEliAmtRecommended = (evt, item) => {
//   // 1) strip non-digits
//   const digits = evt.target.value.replace(/\D/g, '')
//   // 2) update the reactive model
//   item.eliAmountRecommended = digits ? parseInt(digits, 10) : 0
//   // 3) write back formatted
//   evt.target.value = item.eliAmountRecommended
//     .toString()
//     .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
// }


// const onInputEliAmtRecommended = (evt, item) => {
//   // 1) strip non-digits
//   const digits = evt.target.value.replace(/\D/g, '')
//   const newValue = digits ? parseInt(digits, 10) : 0

//   // 2) Validate against amount required
//   if (newValue > item.amountRequired) {
//     // Show validation error
//     Swal.fire({
//       icon: 'warning',
//       title: 'Invalid Amount',
//       text: `Amount Recommended (₦${newValue.toLocaleString()}) cannot exceed Amount Required (₦${item.amountRequired.toLocaleString()})`,
//       confirmButtonText: 'OK'
//     })

//     // Reset to amount required or previous valid value
//     item.eliAmountRecommended = Math.min(item.eliAmountRecommended || 0, item.amountRequired)
//     evt.target.value = item.eliAmountRecommended
//       .toString()
//       .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
//     return
//   }

//   // 3) update the reactive model
//   item.eliAmountRecommended = newValue

//   // 4) write back formatted
//   evt.target.value = item.eliAmountRecommended
//     .toString()
//     .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
// }


// function formatCurrency(val) {
//   if (val === '' || val === null || val === undefined) return '';
//   const n = Number(val);
//   if (isNaN(n)) return '';
//   return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// }

/**
 * displayValue used in the template to show the current eliAmountRecommended.
 * Uses formatCurrency so the table cell shows "34,448.48" style strings.
 */
// const displayValue = (item) => {
//   // item.eliAmountRecommended might be undefined; show empty when absent
//   const n = item.eliAmountRecommended ?? 0;
//   // If you prefer blank when zero: return n === 0 ? '' : formatCurrency(n)
//   return formatCurrency(n);
// }


</script>

<style scoped>
.warrant-content-area {
  min-height: 360px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
}

.budget-item-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  /* forces every .col to match the tallest */
}

.budget-item-wrapper .view-only-input,
.budget-item-wrapper input.view-only-input {
  flex: 1;
  /* inputs (and your Approval‐Authority <div>) all grow equally */
  /* for your Approval Authority div: */
  white-space: pre-wrap;
}

.dashboard-layout .warrant-item-wrapper {
  margin-top: 20px;
  padding: 0 20px;
}

.dashboard-layout .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 1rem;
}

.dashboard-layout .section-border {
  /* padding: 1rem; */
  border-top: 3px solid #667eea !important;
}


.dashboard-layout .warrant-item-wrapper .col {
  border-right: 1px solid var(--border);
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05); */
}

.dashboard-layout .warrant-item-wrapper .col.no-border {
  border-right: none !important;
}

.dashboard-layout .warrant-item-wrapper.step-3 .fixed-label {
  min-height: 80px;
  justify-content: flex-start;
  width: 350px;
  box-sizing: border-box;
  text-align: left;
  /* all: unset; */
  display: flex;
  margin-bottom: 0;
  flex-shrink: 0;
  margin-left: 1rem;
}

.dashboard-layout .budget-item-wrapper.step-3 .col {
  padding: 0;
}

.dashboard-layout .budget-item-wrapper.step-3 .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  /* padding: 1rem; */
}

.dashboard-layout .budget-item-wrapper.step-3 .content {
  border-bottom: 2px solid var(--border);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  gap: 1rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.dashboard-layout .budget-item-wrapper.step-3 label {
  font-size: 16px;
  padding: .375rem 0 !important;
  margin-bottom: 0px !important;
  padding-bottom: 0.5rem;
  font-weight: 600;
}

.dashboard-layout .budget-item-wrapper .col-md-3.no-border {
  border: none;
}

.dashboard-layout .budget-item-wrapper .col-md-3.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .budget-item-wrapper .col-md-2.no-border {
  border: none;
}

.dashboard-layout .budget-item-wrapper .col-md-2.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .fixed-label {
  display: flex;
  align-items: center;
  /* vertical center */
  justify-content: center;
  /* horizontal center */
  min-height: 4rem;
  width: 100%;
  padding: 0 0.25rem;
  box-sizing: border-box;
  text-align: center;
}

.dashboard-layout .form-outer-wrapper a {
  text-decoration: underline;
  text-decoration-style: italic;
  color: #667eea;
  font-weight: 600;
}

.disabled-generate-btn {
  opacity: 0.55;
}

/* PV Modal Styling */
.pv-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.pv-modal {
  background: #ffffff;
  width: min(1100px, 100%);
  max-height: 85vh;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.pv-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.pv-modal-header h5 {
  font-weight: 600;
  color: #1f2937;
}

.pv-modal-body {
  padding: 1.25rem;
  overflow: auto;
}

.pv-modal-body .table {
  margin-bottom: 0;
}

.pv-modal-body .badge {
  padding: 0.375rem 0.75rem;
  font-weight: 500;
}

@media (max-width: 768px) {
  .pv-modal {
    width: 100%;
    max-height: 90vh;
  }

  .pv-modal-body .table thead {
    display: none;
  }

  .pv-modal-body .table tbody {
    display: block;
  }

  .pv-modal-body .table tbody tr {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .pv-modal-body .table tbody td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border: none;
    border-bottom: 1px solid #f3f4f6;
    text-align: left !important;
  }

  .pv-modal-body .table tbody td:last-child {
    border-bottom: none;
    justify-content: center;
  }

  .pv-modal-body .table tbody td::before {
    content: attr(data-label);
    font-weight: 600;
    color: #374151;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .pv-modal-body .table tbody td:last-child::before {
    content: 'Status: ';
  }
}
</style>
